import React, { useCallback, useEffect, useLayoutEffect, useState, useRef, useMemo } from 'react';
import { GameState, UserLanguage, gameApi } from '../api';
import { GameInfo } from './GameInfo';
import { NoGameMessage } from './NoGameMessage';
import { usePolling } from '../utils/usePolling';
import { isAbortError } from '../utils/isAbortError';
import { jsonEqual } from '../utils/jsonEqual';
import { findInvestigator, getFirstControlledInvestigator } from '../utils/investigators';

const resolveControlledInvestigators = (state: GameState | null, playerName: string): string[] => {
  if (!state || !playerName) {
    return [];
  }

  const controlledFromPlayer =
    state.players?.find(p => p.name === playerName)?.controlledInvestigators ?? [];
  const merged = [...controlledFromPlayer];

  const deduped = merged.filter((name, index) =>
    !!name && merged.findIndex(other => other.toLowerCase() === name.toLowerCase()) === index
  );

  const turnOrder = state.investigatorTurnOrder ?? [];
  if (turnOrder.length === 0) {
    return deduped;
  }

  const orderByName = new Map(turnOrder.map((name, index) => [name.toLowerCase(), index]));
  return [...deduped].sort((a, b) => {
    const aIndex = orderByName.get(a.toLowerCase());
    const bIndex = orderByName.get(b.toLowerCase());
    if (aIndex === undefined && bIndex === undefined) return 0;
    if (aIndex === undefined) return 1;
    if (bIndex === undefined) return -1;
    return aIndex - bIndex;
  });
};

const resolveCurrentPlayerName = (
  state: GameState | null,
  playerName: string,
  guestUserId?: string | null
): string => {
  if (!state) {
    return playerName;
  }

  const normalizedPlayerName = playerName.trim().toLowerCase();
  const playerByName = state.players?.find(
    player => player.name.trim().toLowerCase() === normalizedPlayerName
  );
  if (playerByName) {
    return playerByName.name;
  }

  const normalizedGuestUserId = guestUserId?.trim().toLowerCase();
  if (!normalizedGuestUserId) {
    return playerName;
  }

  return (
    state.players?.find(
      player => player.userId?.trim().toLowerCase() === normalizedGuestUserId
    )?.name ?? playerName
  );
};

const resolveActionFocusedInvestigator = (
  state: GameState | null,
  controlledInvestigators: string[],
  currentSelection?: string
): string | undefined => {
  if (!state || controlledInvestigators.length === 0) {
    return currentSelection;
  }

  const controlledByName = new Map(
    controlledInvestigators.map(name => [name.toLowerCase(), name])
  );
  const actionableInvestigators = new Set<string>();

  for (const action of state.availableActions ?? []) {
    if (!action.investigatorName || action.cardType === 'Investigator') {
      continue;
    }

    const controlledName = controlledByName.get(action.investigatorName.toLowerCase());
    if (controlledName) {
      actionableInvestigators.add(controlledName.toLowerCase());
    }
  }

  if (actionableInvestigators.size === 0) {
    return currentSelection;
  }

  const orderedActionable = controlledInvestigators.filter(name =>
    actionableInvestigators.has(name.toLowerCase())
  );
  if (orderedActionable.length === 0) {
    return currentSelection;
  }

  if (
    currentSelection &&
    orderedActionable.some(name => name.toLowerCase() === currentSelection.toLowerCase())
  ) {
    return currentSelection;
  }

  return orderedActionable[0];
};

interface GameViewProps {
  openedActionSessions?: Set<string>;
  currentGameId?: string;
  loading: boolean;
  playerName: string;
  guestUserId?: string | null;
  userLanguage?: UserLanguage;
  message?: string;
  authRole?: string;
  onStartGame: () => void;
  onJoinGame: () => void;
  onExecuteAction: (actionId: string) => Promise<GameState | null>;
  onGameIdChange: (gameId: string) => void;
  onMessage: (message: string) => void;
  onError: (error: string | null) => void;
}

export const GameView: React.FC<GameViewProps> = ({
  openedActionSessions,
  currentGameId,
  loading,
  playerName,
  guestUserId,
  userLanguage,
  message,
  authRole,
  onStartGame,
  onJoinGame,
  onExecuteAction,
  onGameIdChange,
  onMessage,
  onError,
}) => {
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [hasCompletedInitialGameLoad, setHasCompletedInitialGameLoad] = useState(false);
  const [selectedInvestigatorByGame, setSelectedInvestigatorByGame] = useState<Record<string, string>>({});
  const selectedInvestigator = currentGameId ? selectedInvestigatorByGame[currentGameId] : undefined;
  const spinnerFrames = useMemo(() => ['◡◡', '⊙⊙', '◠◠'], []);
  const spinnerIndexRef = useRef(0);
  const spinnerIntervalRef = useRef<number | undefined>(undefined);
  const currentGameStatusRef = useRef<string | undefined>(undefined);
  const stateRequestVersionRef = useRef(0);
  const actionInFlightRef = useRef(false);
  const [actionStatus, setActionStatus] = useState<'idle' | 'submitting' | 'settled'>('idle');

  useLayoutEffect(() => {
    if (actionStatus === 'settled') {
      actionInFlightRef.current = false;
      setActionStatus('idle');
    }
  }, [actionStatus]);

  const clearSpinnerTitle = useCallback(() => {
    if (spinnerIntervalRef.current !== undefined) {
      window.clearInterval(spinnerIntervalRef.current);
      spinnerIntervalRef.current = undefined;
    }
    document.title = 'Arkham Horror';
  }, []);

  const startSpinnerTitle = useCallback(() => {
    if (spinnerIntervalRef.current !== undefined) {
      return;
    }

    document.title = `${spinnerFrames[spinnerIndexRef.current]} Arkham Horror`;
    spinnerIntervalRef.current = window.setInterval(() => {
      spinnerIndexRef.current = (spinnerIndexRef.current + 1) % spinnerFrames.length;
      document.title = `${spinnerFrames[spinnerIndexRef.current]} Arkham Horror`;
    }, 1000);
  }, [spinnerFrames]);

  useEffect(() => {
    currentGameStatusRef.current = gameState?.status;
  }, [gameState?.status]);

  const applyGameState = useCallback((state: GameState) => {
    setGameState(previous => jsonEqual(previous, state) ? previous : state);
    currentGameStatusRef.current = state.status;
    onMessage(state.data?.message || "");

    if (state.gameId && state.gameId !== currentGameId) {
      onGameIdChange(state.gameId);
    }

    const currentPlayerName = resolveCurrentPlayerName(state, playerName, guestUserId);
    const controlled = resolveControlledInvestigators(state, currentPlayerName);
    if (state.gameId) {
      setSelectedInvestigatorByGame(prev => {
        const next = { ...prev };
        if (controlled.length > 0) {
          const existing = next[state.gameId];
          const existingControlled = existing
            ? controlled.find(name => name.toLowerCase() === existing.toLowerCase())
            : undefined;
          const fallbackSelection = existingControlled ?? controlled[0];
          const focusedSelection = resolveActionFocusedInvestigator(
            state,
            controlled,
            fallbackSelection
          );
          next[state.gameId] = focusedSelection ?? fallbackSelection;
        } else {
          delete next[state.gameId];
        }
        return jsonEqual(prev, next) ? prev : next;
      });
    }

    const playerCount =
      state.players?.length ??
      state.playersDetailed?.length ??
      state.playersOrder?.length ??
      0;

    const normalizedPlayerName = currentPlayerName.trim().toLowerCase();
    const isCurrentPlayerActive =
      state.status === 'WaitingForAction' &&
      !!state.activePlayer &&
      !!normalizedPlayerName &&
      state.activePlayer.trim().toLowerCase() === normalizedPlayerName;

    if (isCurrentPlayerActive && playerCount > 1) {
      startSpinnerTitle();
    } else {
      clearSpinnerTitle();
    }
  }, [clearSpinnerTitle, currentGameId, guestUserId, onGameIdChange, onMessage, playerName, startSpinnerTitle]);

  const fetchGameState = useCallback(async (forceLoad = false, signal?: AbortSignal) => {
    if (!playerName.trim() || actionInFlightRef.current) {
      return;
    }
    const requestVersion = ++stateRequestVersionRef.current;
    try {
      onError(null);
      if (currentGameStatusRef.current === 'Finished' && !forceLoad) {
        return;
      }
      const state = await gameApi.getGameState(playerName, currentGameId, signal);
      if (
        actionInFlightRef.current
        || requestVersion !== stateRequestVersionRef.current
      ) {
        return;
      }
      setHasCompletedInitialGameLoad(true);
      if (!state) {
        return;
      }
      applyGameState(state);
    } catch (err) {
      if (actionInFlightRef.current || requestVersion !== stateRequestVersionRef.current) {
        return;
      }
      if (isAbortError(err, signal)) {
        return;
      }
      onError(err instanceof Error ? err.message : 'An error occurred');
    }
  }, [applyGameState, currentGameId, onError, playerName]);

  const handleJoinGame = useCallback(async () => {
    await Promise.resolve(onJoinGame());
    await fetchGameState();
  }, [fetchGameState, onJoinGame]);

  const handleExecuteAction = useCallback(async (actionId: string) => {
    if (actionInFlightRef.current) return;
    actionInFlightRef.current = true;
    setActionStatus('submitting');
    stateRequestVersionRef.current += 1;
    try {
      const state = await onExecuteAction(actionId);
      if (state) {
        applyGameState(state);
      }
    } finally {
      // Keep the synchronous guard until the response state has committed.
      setActionStatus('settled');
    }
  }, [applyGameState, onExecuteAction]);

  const pollGameState = useCallback((signal?: AbortSignal) => {
    return fetchGameState(false, signal);
  }, [fetchGameState]);

  useEffect(() => {
    const handleFocus = () => {
      clearSpinnerTitle();
    };
    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [clearSpinnerTitle]);

  useEffect(() => {
    return () => clearSpinnerTitle();
  }, [clearSpinnerTitle]);

  usePolling({
    fetcher: pollGameState,
    intervalMs: 4000,
    hiddenDelayMs: 15000,
  });

  if (!gameState) {
    return <NoGameMessage loading={!hasCompletedInitialGameLoad} onStartGame={onStartGame} />;
  }

  const currentPlayerName = resolveCurrentPlayerName(gameState, playerName, guestUserId);
  const controlledInvestigators = resolveControlledInvestigators(gameState, currentPlayerName);
  const investigatorDetails =
    findInvestigator(gameState, selectedInvestigator) ||
    getFirstControlledInvestigator(gameState, currentPlayerName);
  const availableActions = gameState?.availableActions || [];

  return (
    <GameInfo
      openedActionSessions={openedActionSessions}
      gameState={gameState}
      loading={loading || actionStatus !== 'idle'}
      playerName={currentPlayerName}
      message={message}
      authRole={authRole}
      userLanguage={userLanguage}
      selectedInvestigator={selectedInvestigator}
      controlledInvestigators={controlledInvestigators}
      currentInvestigator={investigatorDetails}
      onJoinGame={handleJoinGame}
      onExecuteAction={handleExecuteAction}
      onRefreshGameState={() => fetchGameState(true)}
      availableActions={availableActions}
      onSelectInvestigator={
        gameState
          ? name =>
              setSelectedInvestigatorByGame(prev => ({
                ...prev,
                [gameState.gameId]: name,
              }))
          : undefined
      }
    />
  );
};

